import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef, MatDialogContent } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { HandoffType } from 'src/app/common/models/handoffType';
import { StoreHandoffService } from 'src/app/services/store-handoff.service';
import { FacilityManager } from 'src/app/managers/facility.manager';
import { AuthManager } from 'src/app/core/auth/auth.manager';
import { IPlannedHandoffException } from 'src/app/common/models/plannedHandoffException';
import { ConfirmPlannedHandoffDeleteComponent } from './confirm-planned-handoff-exception-delete/confirm-planned-handoff-exception-delete.component';
import { SchedulePlannedHandoffExceptionComponent } from '../schedule-planned-handoff-exception/schedule-planned-handoff-exception.component';
import { conditionallyLog } from 'src/app/common/consoleLogHelper';
import { DatePipe, NgClass } from '@angular/common';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatButton } from '@angular/material/button';

@Component({
  selector: 'app-view-planned-handoff-exception',
  templateUrl: './view-planned-handoff-exceptions.component.html',
  styleUrls: ['./view-planned-handoff-exceptions.component.scss'],
  standalone: true,
  imports: [CdkScrollable, MatDialogContent, NgClass, MatButton],
})
export class ViewPlannedHandoffExceptionComponent implements OnInit {
  public handoffType: string = this.data.handoffType;
  public currentFacilityId: string;
  public plannedHandoffExceptions: IPlannedHandoffException[] = [];
  public selectedIndex = -1;

  constructor(
    public dialogRef: MatDialogRef<ViewPlannedHandoffExceptionComponent>,
    private readonly dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: HandoffType,
    private readonly authManager: AuthManager,
    private readonly storeHandoffService: StoreHandoffService,
    private readonly facilityManager: FacilityManager,
    private readonly toastr: ToastrService,
    private readonly datePipe: DatePipe,
  ) {
    this.currentFacilityId = this.facilityManager.getCurrentFacility();
  }

  public ngOnInit(): void {
    this.storeHandoffService.getPlannedHandoffHandoffExceptions(this.currentFacilityId).subscribe({
      next: (plannedHandoffExceptions: IPlannedHandoffException[]) => {
        this.plannedHandoffExceptions = plannedHandoffExceptions
        .filter((exception) => exception.handoffType === this.data.handoffType)
        .sort((a, b) => new Date(a.startOn).getTime() - new Date(b.startOn).getTime());
      },
      error: (error: any) => {
        console.error('Error fetching planned handoff exceptions:', error);
        this.toastr.error('Error fetching planned handoff exceptions.');
      },
    });
  }

  public setSelectedRow(rowIndex: number): void {
    this.selectedIndex = rowIndex;
  }

  public canMakeChanges(): boolean {
    return this.authManager.isCurrentAccountAdminOrITRole();
  }

  public onNoClick(): void {
    this.dialogRef.close();
  }

  public editAction(plannedHandoffException: IPlannedHandoffException): void {
    const dialogRef = this.dialog.open(SchedulePlannedHandoffExceptionComponent, {
      width: '700px',
      maxHeight: '700px',
      maxWidth: '700px',
      data: { handoffType: this.data.handoffType, exceptionToEdit: plannedHandoffException },
    });

    dialogRef.afterClosed().subscribe((result: IPlannedHandoffException) => {
      if (result) {
        conditionallyLog('Updated Planned Handoff Exception', result);
        // Update the plannedHandoffExceptions array with the new data
        const index = this.plannedHandoffExceptions.findIndex(
          (exception) => exception.exceptionId === result.exceptionId,
        );
        if (index !== -1) {
          this.plannedHandoffExceptions[index] = result;
        }
      }
    });
  }

  public deleteOverrideConfirm(plannedHandoffException: IPlannedHandoffException): void {
    const confirmDialogRef = this.dialog.open(ConfirmPlannedHandoffDeleteComponent, {
      width: '340px',
      height: '245px',
      data: plannedHandoffException,
    });

    confirmDialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        this.deletePlannedHandoffException(plannedHandoffException);
      }
    });
  }

  public formattedExceptionDate(plannedHandoffException: IPlannedHandoffException): string {
    const start = plannedHandoffException?.startOn;
    const end = plannedHandoffException?.endOn;

    if (!start) return '';

    const startDateStr = this.datePipe.transform(start, 'EEE, MMMM d, y');
    const endDateStr = this.datePipe.transform(end, 'EEE, MMMM d, y');

    const sameDay = this.datePipe.transform(start, 'yyyy-MM-dd') === this.datePipe.transform(end, 'yyyy-MM-dd');

    return sameDay || !endDateStr ? startDateStr! : `${startDateStr} - ${endDateStr}`;
  }

  private deletePlannedHandoffException(plannedHandoffException: IPlannedHandoffException): void {
    this.storeHandoffService
      .deletePlannedHandoffHandoffException(this.currentFacilityId, plannedHandoffException.exceptionId!)
      .subscribe({
        next: (result) => {
          this.toastr.success('Planned Handoff Outage removed');
          // Remove the deleted exception from the array
          this.plannedHandoffExceptions = this.plannedHandoffExceptions.filter(
            (exception) => exception.exceptionId !== plannedHandoffException.exceptionId,
          );
        },
        error: () => {
          this.toastr.error('Error while Creating Outage');
        },
      });
  }
}
